import React, { useContext, useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { AppContext } from "../../store/AppContext";
import { styled } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  "https://asia-south1-resmenu-c1b90.cloudfunctions.net/api";

// Match backend live-order bucketing (Asia/Kolkata) so graph X-axis keys align.
const formatISTDate = (date) =>
  new Date(date).toLocaleString("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

const formatLocalDate = (date) => formatISTDate(date);

const parseLocalDate = (dateString) => {
  const [y, m, d] = dateString.split("-").map(Number);
  // Noon IST avoids DST/UTC edge cases when converting calendar picks.
  return new Date(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}T12:00:00+05:30`);
};

const endOfLocalDay = (date) => {
  const ist = formatISTDate(date);
  return new Date(`${ist}T23:59:59.999+05:30`);
};

const getOrderTimestampMs = (order) => {
  const raw = order?.timestamp || order?.createdAt;
  if (!raw) return null;
  if (typeof raw.toDate === "function") return raw.toDate().getTime();
  if (raw.seconds) return raw.seconds * 1000;
  const parsed = new Date(raw).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

const emptyDayStats = (date) => ({
  date,
  totalOrders: 0,
  totalRevenue: 0,
  storeViews: 0,
  created: 0,
  pending: 0,
  paid: 0,
  accepted: 0,
  shipped: 0,
  delivered: 0,
  cancelled: 0,
  expired: 0,
  rejected: 0,
  completed: 0,
});

const fillDateRange = (stats, start, end, viewsByDate = {}) => {
  const byDate = {};
  (stats || []).forEach((day) => {
    if (!day?.date) return;
    byDate[day.date] = {
      ...day,
      storeViews: viewsByDate[day.date] || day.storeViews || 0,
    };
  });

  // Walk IST calendar days — same keys the live-orders API uses.
  const filled = [];
  const startStr = formatISTDate(start);
  const endStr = formatISTDate(end);
  const cur = new Date(`${startStr}T00:00:00+05:30`);
  const endDay = new Date(`${endStr}T00:00:00+05:30`);

  while (cur <= endDay) {
    const dateStr = formatISTDate(cur);
    filled.push(
      byDate[dateStr] || {
        ...emptyDayStats(dateStr),
        storeViews: viewsByDate[dateStr] || 0,
      }
    );
    cur.setTime(cur.getTime() + 24 * 60 * 60 * 1000);
  }

  return filled;
};

/** Attach store views to API day buckets without re-keying dates. */
const mapStatsToGraph = (stats, viewsByDate = {}) =>
  (stats || []).map((day) => ({
    ...day,
    date: day.date,
    totalOrders: day.totalOrders || 0,
    totalRevenue: day.totalRevenue || 0,
    storeViews: viewsByDate[day.date] || day.storeViews || 0,
    created: day.created || 0,
    pending: day.pending || 0,
    paid: day.paid || 0,
    accepted: day.accepted || 0,
    shipped: day.shipped || 0,
    delivered: day.delivered || 0,
    cancelled: day.cancelled || 0,
    expired: day.expired || 0,
    rejected: day.rejected || 0,
    completed: day.completed || 0,
  }));

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
  ...theme.applyStyles("dark", {
    backgroundColor: "#1A2027",
  }),
}));

const DashboardTab = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));

  const [statusFilter, setStatusFilter] = useState("All");
  const [orders, setOrders] = useState([]);
  const [orderCounts, setOrderCounts] = useState({
    total: 0,
    created: 0,
    pending: 0,
    paid: 0,
    accepted: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0,
    expired: 0,
    rejected: 0,
    completed: 0,
  });
  const [dailyStats, setDailyStats] = useState([]);
  const { setHeaderData, setOrderTabInfo, setSelectedTab } =
    useContext(AppContext);
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 7))
  );
  const [endDate, setEndDate] = useState(new Date());
  const [anchorEl, setAnchorEl] = useState(null);
  const [dateRangeLabel, setDateRangeLabel] = useState("Last 7 Days");
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");

  // Date range presets
  const datePresets = [
    {
      label: "Today",
      getDates: () => {
        const today = new Date();
        return { start: new Date(today.setHours(0, 0, 0, 0)), end: new Date() };
      },
    },
    {
      label: "Yesterday",
      getDates: () => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        return {
          start: new Date(yesterday.setHours(0, 0, 0, 0)),
          end: new Date(yesterday.setHours(23, 59, 59, 999)),
        };
      },
    },
    {
      label: "Last 7 Days",
      getDates: () => {
        const start = new Date();
        start.setDate(start.getDate() - 7);
        return { start, end: new Date() };
      },
    },
    {
      label: "Last 30 Days",
      getDates: () => {
        const start = new Date();
        start.setDate(start.getDate() - 30);
        return { start, end: new Date() };
      },
    },
    {
      label: "This Month",
      getDates: () => {
        const start = new Date();
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        return { start, end: new Date() };
      },
    },
    {
      label: "Last Month",
      getDates: () => {
        const start = new Date();
        start.setMonth(start.getMonth() - 1);
        start.setDate(1);
        start.setHours(0, 0, 0, 0);
        const end = new Date();
        end.setDate(0);
        end.setHours(23, 59, 59, 999);
        return { start, end };
      },
    },
    { label: "Custom Range", getDates: () => null },
  ];

  useEffect(() => {
    fetchData();
  }, [startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    setFetchError("");
    await Promise.allSettled([fetchOrders(), fetchDailyStats()]);
    setLoading(false);
  };

  const fetchOrders = async () => {
    try {
      const rangeStart = startDate.getTime();
      const rangeEnd = endDate.getTime();

      // Avoid composite index on timestamp range + orderBy — fetch recent and filter.
      const q = query(
        collection(db, "orders"),
        orderBy("timestamp", "desc"),
        limit(50)
      );
      const querySnapshot = await getDocs(q);
      const ordersData = querySnapshot.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .filter((order) => {
          const ts = getOrderTimestampMs(order);
          return ts != null && ts >= rangeStart && ts <= rangeEnd;
        })
        .slice(0, 4);

      setOrders(ordersData);
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders([]);
    }
  };

  const fetchStoreViewsByDate = async () => {
    const viewsByDate = {};
    try {
      const viewsRef = collection(db, "storeViews");
      const q = query(
        viewsRef,
        where("date", ">=", formatLocalDate(startDate)),
        where("date", "<=", formatLocalDate(endDate))
      );
      const viewsSnapshot = await getDocs(q);
      viewsSnapshot.forEach((docSnap) => {
        const viewData = docSnap.data();
        const date = viewData.date;
        if (date) viewsByDate[date] = (viewsByDate[date] || 0) + 1;
      });
    } catch (error) {
      console.warn("Store views unavailable:", error);
    }
    return viewsByDate;
  };

  const fetchDailyStatsFromFirestore = async (viewsByDate) => {
    const statsRef = collection(db, "dailyOrderStats");
    const q = query(
      statsRef,
      where("date", ">=", formatLocalDate(startDate)),
      where("date", "<=", formatLocalDate(endDate)),
      orderBy("date", "asc")
    );
    const snapshot = await getDocs(q);
    const stats = snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));
    return fillDateRange(stats, startDate, endDate, viewsByDate);
  };

  const fetchDailyStats = async () => {
    try {
      const viewsByDate = await fetchStoreViewsByDate();

      // Plain IST calendar dates — avoids UTC end-of-day spilling into tomorrow.
      const params = new URLSearchParams({
        startDate: formatISTDate(startDate),
        endDate: formatISTDate(endDate),
      });

      let combinedStats = null;

      try {
        const response = await fetch(
          `${API_BASE_URL}/payment/daily-stats?${params.toString()}`
        );
        const data = await response.json();

        if (response.ok && data.success && Array.isArray(data.stats)) {
          // Live-orders API already returns one bucket per IST day — map
          // straight onto the graph, then ensure the selected range is filled.
          const mapped = mapStatsToGraph(data.stats, viewsByDate);
          combinedStats = fillDateRange(
            mapped,
            startDate,
            endDate,
            viewsByDate
          );
        }
      } catch (apiError) {
        console.warn("Daily stats API unavailable, using Firestore:", apiError);
      }

      if (!combinedStats) {
        combinedStats = await fetchDailyStatsFromFirestore(viewsByDate);
      }

      setDailyStats(combinedStats);
      calculateOrderCounts(combinedStats);
    } catch (error) {
      console.error("Error fetching daily stats:", error);
      setFetchError("Could not load chart data. Check console for details.");
      setDailyStats(fillDateRange([], startDate, endDate));
      calculateOrderCounts([]);
    }
  };

  const calculateOrderCounts = (stats) => {
    const counts = {
      total: 0,
      created: 0,
      pending: 0,
      paid: 0,
      accepted: 0,
      shipped: 0,
      delivered: 0,
      cancelled: 0,
      expired: 0,
      rejected: 0,
      completed: 0,
    };

    stats.forEach((day) => {
      counts.total += day.totalOrders || 0;
      counts.created += day.created || 0;
      counts.pending += day.pending || 0;
      counts.paid += day.paid || 0;
      counts.accepted += day.accepted || 0;
      counts.shipped += day.shipped || 0;
      counts.delivered += day.delivered || 0;
      counts.cancelled += day.cancelled || 0;
      counts.expired += day.expired || 0;
      counts.rejected += day.rejected || 0;
      counts.completed += day.completed || 0;
    });

    setOrderCounts(counts);
  };

  const handleDatePresetClick = (preset) => {
    if (preset.getDates()) {
      const { start, end } = preset.getDates();
      setStartDate(start);
      setEndDate(end);
      setDateRangeLabel(preset.label);
    }
    setAnchorEl(null);
  };

  const handleCustomRangeChange = () => {
    setDateRangeLabel("Custom Range");
    setAnchorEl(null);
  };

  const formatDateRange = () => {
    if (dateRangeLabel !== "Custom Range") {
      return dateRangeLabel;
    }
    return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
  };

  // Responsive Status card component
  const StatusCard = ({ title, count, color }) => (
    <div
      style={{
        flex: isMobile ? "1 1 100%" : isTablet ? "1 1 45%" : "1 1 150px",
        minWidth: isMobile ? "100%" : "150px",
        background: "white",
        padding: isMobile ? "12px" : "15px",
        borderRadius: "8px",
        boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
        borderLeft: `4px solid ${color}`,
        margin: "5px",
      }}
    >
      <h4
        style={{
          color: "#64748B",
          fontSize: isMobile ? "0.8rem" : "0.9rem",
          margin: "0 0 5px 0",
        }}
      >
        {title}
      </h4>
      <h3
        style={{
          color: "#1E293B",
          fontSize: isMobile ? "1.2rem" : "1.5rem",
          margin: "0",
          fontWeight: "600",
        }}
      >
        {count}
      </h3>
    </div>
  );

  useEffect(() => {
    setHeaderData((prevState) => ({
      ...prevState,
      leftText: "Dashboard",
      back: true,
      leftAction: () => {},
      rightAction: () => {},
    }));
  }, []);

  const getStatusBackgroundColor = (status) => {
    switch (status?.toUpperCase()) {
      case "CREATED":
        return "rgba(59, 130, 246, 0.1)";
      case "PENDING":
        return "rgba(234, 179, 8, 0.1)";
      case "ACCEPTED":
        return "rgba(16, 185, 129, 0.1)";
      case "SHIPPED":
        return "rgba(139, 92, 246, 0.1)";
      case "REJECTED":
        return "rgba(239, 68, 68, 0.1)";
      case "COMPLETED":
        return "rgba(5, 150, 105, 0.1)";
      case "CANCELLED":
        return "rgba(107, 114, 128, 0.1)";
      default:
        return "rgba(59, 130, 246, 0.1)";
    }
  };

  const getStatusTextColor = (status) => {
    switch (status?.toUpperCase()) {
      case "CREATED":
        return "#3B82F6";
      case "PENDING":
        return "#EAB308";
      case "ACCEPTED":
        return "#10B981";
      case "SHIPPED":
        return "#8B5CF6";
      case "REJECTED":
        return "#EF4444";
      case "COMPLETED":
        return "#059669";
      case "CANCELLED":
        return "#6B7280";
      default:
        return "#3B82F6";
    }
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: isMobile ? "15px" : "20px",
        width: "100%",
        maxWidth: isLargeScreen ? "1400px" : "100%",
        margin: "auto",
        padding: isMobile ? "0" : "0 10px",
      }}
    >
      {/* Header with Date Range Selector */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          gap: isMobile ? "10px" : "0",
        }}
      >
        <h3 style={{ fontWeight: "500", margin: isMobile ? "0" : "0" }}>
          Overview
        </h3>
        <div
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            flexWrap: isMobile ? "wrap" : "nowrap",
            width: isMobile ? "100%" : "auto",
          }}
        >
          <Button
            variant="outlined"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            style={{
              textTransform: "none",
              fontSize: isMobile ? "0.8rem" : "1rem",
              width: isMobile ? "100%" : "auto",
            }}
          >
            {formatDateRange()}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            {datePresets.map((preset) => (
              <MenuItem
                key={preset.label}
                onClick={() =>
                  preset.label === "Custom Range"
                    ? handleCustomRangeChange()
                    : handleDatePresetClick(preset)
                }
              >
                {preset.label}
              </MenuItem>
            ))}
          </Menu>
          {dateRangeLabel === "Custom Range" && (
            <>
              <input
                type="date"
                value={formatLocalDate(startDate)}
                onChange={(e) => setStartDate(parseLocalDate(e.target.value))}
                style={{
                  padding: isMobile ? "6px" : "8px",
                  borderRadius: "4px",
                  border: "1px solid #ddd",
                  fontSize: isMobile ? "0.8rem" : "1rem",
                  width: isMobile ? "calc(50% - 15px)" : "auto",
                }}
              />
              <span style={{ display: isMobile ? "none" : "inline" }}>to</span>
              <input
                type="date"
                value={formatLocalDate(endDate)}
                onChange={(e) =>
                  setEndDate(endOfLocalDay(parseLocalDate(e.target.value)))
                }
                style={{
                  padding: isMobile ? "6px" : "8px",
                  borderRadius: "4px",
                  border: "1px solid #ddd",
                  fontSize: isMobile ? "0.8rem" : "1rem",
                  width: isMobile ? "calc(50% - 15px)" : "auto",
                }}
              />
            </>
          )}
        </div>
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: "20px" }}>
          Loading data...
        </div>
      )}

      {fetchError && !loading && (
        <div
          style={{
            padding: "12px 16px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: "8px",
            color: "#b91c1c",
            fontSize: "0.9rem",
          }}
        >
          {fetchError}
        </div>
      )}

      {/* Order Status Summary Cards */}
      <div style={{ width: "100%" }}>
        <h3
          style={{
            fontWeight: "400",
            color: "black",
            marginBottom: isMobile ? "15px" : "20px",
            fontSize: isMobile ? "1rem" : "1.17rem",
          }}
        >
          Order Status Summary ({formatDateRange()})
        </h3>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: isMobile ? "5px" : "10px",
            margin: isMobile ? "-5px" : "0",
          }}
        >
          <StatusCard
            title="Total Orders"
            count={orderCounts.total}
            color="#6366F1"
          />
          <StatusCard
            title="Created"
            count={orderCounts.created}
            color="#3B82F6"
          />
          <StatusCard
            title="Pending"
            count={orderCounts.pending}
            color="#F59E0B"
          />
          <StatusCard
            title="Accepted"
            count={orderCounts.accepted}
            color="#06B6D4"
          />
          <StatusCard
            title="Shipped"
            count={orderCounts.shipped}
            color="#8B5CF6"
          />
        </div>
      </div>

      {/* Revenue Overview Section */}
      <div
        style={{
          width: "100%",
          background: "white",
          padding: isMobile ? "15px" : "23px",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
          borderRadius: "10px",
        }}
      >
        <h3
          style={{
            fontWeight: "400",
            color: "black",
            marginBottom: isMobile ? "15px" : "20px",
            fontSize: isMobile ? "1rem" : "1.17rem",
          }}
        >
          Revenue Overview ({formatDateRange()})
        </h3>

        {/* Revenue Summary Cards */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: isMobile ? "10px" : "15px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              flex: isMobile ? "1 1 100%" : isTablet ? "1 1 45%" : "1 1 200px",
              minWidth: isMobile ? "100%" : "200px",
              background: "#f0f9ff",
              padding: isMobile ? "12px" : "15px",
              borderRadius: "8px",
              borderLeft: "4px solid #0369a1",
            }}
          >
            <h4
              style={{
                color: "#64748B",
                margin: "0 0 5px 0",
                fontSize: isMobile ? "0.8rem" : "0.9rem",
              }}
            >
              Total Revenue
            </h4>
            <h2
              style={{
                color: "#1E293B",
                fontSize: isMobile ? "1.4rem" : "1.8rem",
                margin: "0",
              }}
            >
              ₹
              {dailyStats
                .reduce((sum, day) => sum + (day.totalRevenue || 0), 0)
                .toLocaleString("en-IN")}
            </h2>
          </div>

          <div
            style={{
              flex: isMobile ? "1 1 100%" : isTablet ? "1 1 45%" : "1 1 200px",
              minWidth: isMobile ? "100%" : "200px",
              background: "#f0fdf4",
              padding: isMobile ? "12px" : "15px",
              borderRadius: "8px",
              borderLeft: "4px solid #059669",
            }}
          >
            <h4
              style={{
                color: "#64748B",
                margin: "0 0 5px 0",
                fontSize: isMobile ? "0.8rem" : "0.9rem",
              }}
            >
              Avg. Order Value
            </h4>
            <h2
              style={{
                color: "#1E293B",
                fontSize: isMobile ? "1.4rem" : "1.8rem",
                margin: "0",
              }}
            >
              ₹
              {(
                dailyStats.reduce(
                  (sum, day) => sum + (day.totalRevenue || 0),
                  0
                ) /
                (dailyStats.reduce(
                  (sum, day) => sum + (day.totalOrders || 0),
                  0
                ) || 1)
              ).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
            </h2>
          </div>

          <div
            style={{
              flex: isMobile ? "1 1 100%" : isTablet ? "1 1 45%" : "1 1 200px",
              minWidth: isMobile ? "100%" : "200px",
              background: "#fff7ed",
              padding: isMobile ? "12px" : "15px",
              borderRadius: "8px",
              borderLeft: "4px solid #ea580c",
            }}
          >
            <h4
              style={{
                color: "#64748B",
                margin: "0 0 5px 0",
                fontSize: isMobile ? "0.8rem" : "0.9rem",
              }}
            >
              Total Orders
            </h4>
            <h2
              style={{
                color: "#1E293B",
                fontSize: isMobile ? "1.4rem" : "1.8rem",
                margin: "0",
              }}
            >
              {dailyStats
                .reduce((sum, day) => sum + (day.totalOrders || 0), 0)
                .toLocaleString("en-IN")}
            </h2>
          </div>

          <div
            style={{
              flex: isMobile ? "1 1 100%" : isTablet ? "1 1 45%" : "1 1 200px",
              minWidth: isMobile ? "100%" : "200px",
              background: "#fef2f2",
              padding: isMobile ? "12px" : "15px",
              borderRadius: "8px",
              borderLeft: "4px solid #dc2626",
            }}
          >
            <h4
              style={{
                color: "#64748B",
                margin: "0 0 5px 0",
                fontSize: isMobile ? "0.8rem" : "0.9rem",
              }}
            >
              Highest Day
            </h4>
            <h2
              style={{
                color: "#1E293B",
                fontSize: isMobile ? "1.4rem" : "1.8rem",
                margin: "0",
              }}
            >
              ₹
              {dailyStats.length > 0
                ? Math.max(
                    ...dailyStats.map((day) => day.totalRevenue || 0)
                  ).toLocaleString("en-IN")
                : "0"}
            </h2>
            <p
              style={{
                color: "#64748B",
                margin: "5px 0 0 0",
                fontSize: isMobile ? "0.7rem" : "0.8rem",
              }}
            >
              {dailyStats.length > 0
                ? dailyStats.find(
                    (day) =>
                      day.totalRevenue ===
                      Math.max(...dailyStats.map((d) => d.totalRevenue || 0))
                  )?.date || ""
                : "—"}
            </p>
          </div>
        </div>

        <ResponsiveContainer
          width="100%"
          height={isMobile ? 250 : 300}
          minHeight={200}
        >
          <BarChart
            data={dailyStats}
            margin={{
              top: 20,
              right: isMobile ? 10 : 30,
              left: isMobile ? -20 : 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: isMobile ? 10 : 12 }}
              angle={isMobile ? -45 : 0}
              textAnchor={isMobile ? "end" : "middle"}
              height={isMobile ? 60 : 30}
            />
            <YAxis
              tickFormatter={(value) => `₹${value.toLocaleString("en-IN")}`}
              tick={{ fontSize: isMobile ? 10 : 12 }}
            />
            <Tooltip
              formatter={(value) => [
                `₹${value.toLocaleString("en-IN")}`,
                "Revenue",
              ]}
              labelFormatter={(label) => `Date: ${label}`}
              contentStyle={{
                background: "rgba(0, 0, 0, 0.8)",
                border: "none",
                borderRadius: "5px",
                color: "white",
              }}
            />
            {!isMobile && <Legend />}
            <Bar
              dataKey="totalRevenue"
              fill="#0369a1"
              name="Daily Revenue"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Store Views and Total Orders Charts */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          gap: "20px",
        }}
      >
        <div
          style={{
            width: isMobile ? "100%" : "50%",
            height: isMobile ? "300px" : "370px",
            background: "white",
            display: "flex",
            flexDirection: "column",
            padding: isMobile ? "15px" : "23px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
            borderRadius: "10px",
            gap: "10px",
          }}
        >
          <h3
            style={{
              fontWeight: "400",
              color: "black",
              fontSize: isMobile ? "1rem" : "1.17rem",
            }}
          >
            Store Views ({formatDateRange()})
          </h3>
          <h2
            style={{
              color: "black",
              fontSize: isMobile ? "1.5rem" : "2rem",
            }}
          >
            {dailyStats
              .reduce((sum, day) => sum + (day.storeViews || 0), 0)
              .toLocaleString()}
          </h2>
          <ResponsiveContainer
            width="100%"
            height={isMobile ? 200 : 250}
            minHeight={180}
          >
            <BarChart
              data={dailyStats}
              margin={{
                top: 20,
                right: isMobile ? 10 : 30,
                left: isMobile ? -10 : 0,
                bottom: 5,
              }}
              barSize={isMobile ? 15 : 20}
            >
              <defs>
                <linearGradient
                  id="storeViewsGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor="#3B82F6" />
                  <stop offset="100%" stopColor="#1D4ED8" />
                </linearGradient>
              </defs>

              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: isMobile ? 10 : 12 }}
                angle={isMobile ? -45 : 0}
                textAnchor={isMobile ? "end" : "middle"}
                height={isMobile ? 60 : 30}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: isMobile ? 10 : 12 }}
              />
              <Tooltip
                formatter={(value) => [value, "Views"]}
                labelFormatter={(label) => `Date: ${label}`}
                contentStyle={{
                  background: "rgba(0, 0, 0, 0.8)",
                  border: "none",
                  borderRadius: "5px",
                  color: "white",
                }}
              />
              {!isMobile && <Legend />}
              <Bar
                dataKey="storeViews"
                fill="url(#storeViewsGradient)"
                name="Store Views"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div
          style={{
            width: isMobile ? "100%" : "50%",
            height: isMobile ? "300px" : "370px",
            background: "white",
            display: "flex",
            flexDirection: "column",
            padding: isMobile ? "15px" : "23px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
            borderRadius: "10px",
            gap: "10px",
          }}
        >
          <h3
            style={{
              fontWeight: "400",
              color: "black",
              fontSize: isMobile ? "1rem" : "1.17rem",
            }}
          >
            Total Orders ({formatDateRange()})
          </h3>
          <h2
            style={{
              color: "black",
              fontSize: isMobile ? "1.5rem" : "2rem",
            }}
          >
            {orderCounts.total}
          </h2>
          <ResponsiveContainer
            width="100%"
            height={isMobile ? 200 : 250}
            minHeight={180}
          >
            <BarChart
              data={dailyStats}
              margin={{
                top: 20,
                right: isMobile ? 10 : 30,
                left: isMobile ? -10 : 0,
                bottom: 5,
              }}
              barSize={isMobile ? 15 : 20}
            >
              <defs>
                <linearGradient
                  id="totalOrdersGradient"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor="#00C49F" />
                  <stop offset="100%" stopColor="#008B74" />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: isMobile ? 10 : 12 }}
                angle={isMobile ? -45 : 0}
                textAnchor={isMobile ? "end" : "middle"}
                height={isMobile ? 60 : 30}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: isMobile ? 10 : 12 }}
              />
              <Tooltip
                formatter={(value) => [value, "Orders"]}
                labelFormatter={(label) => `Date: ${label}`}
                contentStyle={{
                  background: "rgba(0, 0, 0, 0.8)",
                  border: "none",
                  borderRadius: "5px",
                  color: "white",
                }}
              />
              <Bar
                dataKey="totalOrders"
                fill="url(#totalOrdersGradient)"
                name="Total Orders"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Orders Section */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "10px",
          width: "100%",
        }}
      >
        <h3
          style={{
            fontWeight: "500",
            fontSize: isMobile ? "1rem" : "1.17rem",
          }}
        >
          Recent Orders ({formatDateRange()})
        </h3>
        <h4
          style={{
            fontWeight: "500",
            color: "gray",
            cursor: "pointer",
            fontSize: isMobile ? "0.9rem" : "1rem",
          }}
          onClick={() => {
            setSelectedTab({
              subCategory: { index: 0 },
              category: { title: "Orders", index: 2 },
            });
          }}
        >
          View all
        </h4>
      </div>

      <Grid
        style={{ width: "100%" }}
        container
        spacing={{ xs: 2, md: 3 }}
        columns={{ xs: 1, sm: 2, md: 2, lg: 4 }}
      >
        {orders.map((order) => (
          <Grid item xs={1} key={order.id}>
            <div
              onClick={() => {
                setHeaderData((prevState) => ({
                  ...prevState,
                  leftText: "Order details",
                  rightActionText: "Accept order",
                  back: false,
                  leftAction: () => {
                    setOrderTabInfo((prevState) => ({
                      ...prevState,
                      viewOrder: false,
                      selectedOrder: null,
                    }));
                    setHeaderData({
                      leftText: "",
                      rightActionText: "",
                      leftAction: () => {},
                      rightAction: () => {
                        setOrderTabInfo((prevState) => ({
                          ...prevState,
                          trigger: true,
                        }));
                      },
                    });
                  },
                }));

                setOrderTabInfo((prevState) => ({
                  ...prevState,
                  viewOrder: true,
                  selectedOrder: order,
                }));
                setSelectedTab({
                  subCategory: { index: 0 },
                  category: { title: "Orders", index: 2 },
                });
              }}
              style={{
                cursor: "pointer",
                height: isMobile ? "180px" : "200px",
                width: "100%",
                background: "white",
                boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                borderRadius: "10px",
                padding: isMobile ? "15px" : "20px",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
              }}
              onMouseEnter={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 15px rgba(0, 0, 0, 0.1)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isMobile) {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 2px 10px rgba(0, 0, 0, 0.05)";
                }
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: isMobile ? "8px" : "10px",
                  height: "100%",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <h4
                    style={{
                      fontWeight: "400",
                      fontSize: isMobile ? "0.9rem" : "1rem",
                      margin: 0,
                    }}
                  >
                    Order #{order.id?.slice(0, isMobile ? 6 : 8)}...
                  </h4>
                  <h5
                    style={{
                      color: "gray",
                      fontWeight: "400",
                      fontSize: isMobile ? "0.75rem" : "0.875rem",
                      margin: 0,
                    }}
                  >
                    {(() => {
                      const ts = getOrderTimestampMs(order);
                      return ts
                        ? new Date(ts).toLocaleDateString()
                        : "—";
                    })()}
                  </h5>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: isMobile ? "8px" : "10px",
                      alignItems: "center",
                      flex: 1,
                    }}
                  >
                    <img
                      src={
                        order?.quantity_each?.[0]?.images?.[0] ||
                        "/placeholder.jpg"
                      }
                      style={{
                        width: isMobile ? "70px" : "90px",
                        objectFit: "cover",
                        objectPosition: "center",
                        height: isMobile ? "90px" : "110px",
                        borderRadius: "10px",
                        background: "#f0f0f0",
                      }}
                      alt="Product"
                      onError={(e) => {
                        e.target.src = "/placeholder.jpg";
                      }}
                    />
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: isMobile ? "5px" : "10px",
                      }}
                    >
                      <h5
                        style={{
                          color: "gray",
                          fontWeight: "400",
                          fontSize: isMobile ? "0.8rem" : "1rem",
                          margin: 0,
                        }}
                      >
                        {order?.quantity_each?.length || 0} ITEMS
                      </h5>
                      <h5
                        style={{
                          fontWeight: "500",
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          margin: 0,
                        }}
                      >
                        ₹{order?.items_total?.toLocaleString("en-IN") || 0}
                      </h5>
                    </div>
                  </div>
                  <div
                    style={{
                      background: getStatusBackgroundColor(order.status),
                      padding: isMobile ? "4px 6px" : "5px 8px",
                      borderRadius: "6px",
                      minWidth: isMobile ? "60px" : "80px",
                      textAlign: "center",
                    }}
                  >
                    <h4
                      style={{
                        color: getStatusTextColor(order.status),
                        fontWeight: "600",
                        fontSize: isMobile ? "0.7rem" : "0.85rem",
                        margin: 0,
                        textTransform: "uppercase",
                      }}
                    >
                      {order.status || "CREATED"}
                    </h4>
                  </div>
                </div>
              </div>
            </div>
          </Grid>
        ))}

        {/* Empty state for no orders */}
        {orders.length === 0 && !loading && (
          <div
            style={{
              width: "100%",
              padding: "40px",
              textAlign: "center",
              color: "#666",
            }}
          >
            <h4>No orders found for the selected date range</h4>
          </div>
        )}
      </Grid>
    </div>
  );
};

export default DashboardTab;
